/**
 * System: Begleiter.
 *
 * Zuständigkeit: der Krümel. Er kämpft nicht — er hebt auf, was liegen bleibt.
 * Das ist die diegetische Lösung für die Inventarrückreise (Game-Design §8).
 */

import { TAKT_DAUER } from '@pg/protocol';
import type { BeuteStueck, Welt } from '../typen.js';
import type { Ctx } from './typen.js';
import { bewegeMitKollision } from './spieler.js';
import { abstand, begrenze, richtungVon, vek, winkelVon } from '../vektor.js';

export function begleiter(welt: Welt, ctx: Ctx): void {
  const k = welt.begleiter;
  const s = welt.spieler;

  if (k.zustand === 'unterwegs') {
    k.zurueckIn -= TAKT_DAUER;
    if (k.zurueckIn <= 0) {
      k.zustand = 'folgt';
      k.korb.length = 0;
      k.pos = vek(s.pos.x, s.pos.y);
    }
    return;
  }

  // Nächstes LIEGENGELASSENES Stück suchen — deterministisch nach id.
  //
  // Zwei Bedingungen, beide nötig (siehe Regelwerk, begleiter._hinweis):
  // Das Stück muss eine Weile liegen, UND der Spieler muss weit genug weg
  // sein. Vorher nahm der Krümel alles, sobald es fiel — gemessen die Hälfte
  // aller Funde. Der Spieler bekam sie nie zu Gesicht.
  const rb = ctx.inhalte.regelwerk.begleiter;
  if (k.zustand === 'folgt' && k.korb.length < k.korbMax) {
    let ziel: BeuteStueck | null = null;
    for (const b of welt.beute) {
      if (b.aufgehoben) continue;
      if (welt.takt - b.seit < rb.wartetSekunden * 30) continue;
      if (abstand(b.pos, s.pos) < rb.mindestabstandSpieler) continue;
      if (abstand(b.pos, k.pos) > rb.sammelRadius) continue;
      if (!ziel || b.id < ziel.id) ziel = b;
    }
    if (ziel) {
      k.zustand = 'holt';
      k.zielBeute = ziel.id;
    }
  }

  let ziel = s.pos;
  if (k.zustand === 'holt' && k.zielBeute !== null) {
    const b = welt.beute.find((x) => x.id === k.zielBeute && !x.aufgehoben);
    if (!b) {
      k.zustand = 'folgt';
      k.zielBeute = null;
    } else {
      ziel = b.pos;
      if (abstand(k.pos, b.pos) < 0.5) {
        b.aufgehoben = true;
        welt.ertrag.stuecke++;
        if (b.gegenstand) k.korb.push(b.gegenstand);
        ctx.ereignisse.push({
          art: 'beute-aufgehoben',
          id: b.id,
          name: b.name,
          durchBegleiter: true,
        });
        k.zustand = 'folgt';
        k.zielBeute = null;
      }
    }
  }

  const d = abstand(k.pos, ziel);
  const wunschAbstand = k.zustand === 'holt' ? 0 : 1.4;
  if (d > wunschAbstand) {
    const richtung = richtungVon(k.pos, ziel);
    const tempo = Math.min(6.5, s.tempo * 1.2 + begrenze(d - 2, 0, 4));
    bewegeMitKollision(
      welt,
      k.pos,
      vek(richtung.x * tempo * TAKT_DAUER, richtung.y * tempo * TAKT_DAUER),
      k.radius,
    );
    k.winkel = winkelVon(richtung);
  }
}

/**
 * System: Abschluss.
 *
 * Zuständigkeit: prüfen, ob der Abstieg vorbei ist. Bewusst das letzte System
 * im Takt — alles andere darf vorher noch wirken.
 */
export function abschluss(welt: Welt, ctx: Ctx): void {
  if (welt.stand !== 'laeuft') return;
  if (welt.aufgabe.fortschritt < welt.aufgabe.ziel) return;

  // Der Abstieg endet erst, wenn der Spieler EINSTEIGT — und Einsteigen ist
  // ein Klick, kein Hineinlaufen. Er steht nach dem Kampf genau dort, wo der
  // Gegner fiel; ein Auslöser durch Betreten hätte ihn sofort hochgefahren,
  // mitten in die Beute hinein.
  if (!welt.ausgang) return;
  if (!welt.einsteigen) return;
  welt.einsteigen = false;
  const r = ctx.inhalte.regelwerk.abstieg.foerderkorbRadius;
  if (abstand(welt.spieler.pos, welt.ausgang) > r) return;

  welt.stand = 'geschafft';
  ctx.ereignisse.push({
    art: 'abstieg-geschafft',
    dauer: welt.takt * TAKT_DAUER,
    beute: welt.ertrag.stuecke,
  });
}
